import { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import { BASE_PATHS, ENVIRONMENTS, useIntroVideo } from '@belong/common';
import MultipleBelongEmployeeProfileView from 'components/MultipleBelongEmployeeProfileView/MultipleBelongEmployeeProfileView';
import Space, { SPACE_TYPES } from 'corecomponents/Space/Space';
import Page from 'layouts/Page/Page';
import { useFeatureFlag } from 'hooks/use-account-redesign';
import {
  getInspectionType,
  QUERY_PARAMS,
} from 'pages/PostInspectionFlow/utils';
import { AGREEMENT_FLOW_STRINGS } from 'strings/agreement-flow.strings';
import { formatString } from 'strings';
import { POST_INSPECTION_FLOW_STRINGS } from 'strings/post-inspection-flow';
import PostInspectionContentLayout from '../../../PostInspectionContentLayout/PostInspectionContentLayout';
import { getImprovementsAssignedEmployee } from '../utils';
import { IntroAddressHeader } from './components/intro-address-header/intro-address-header';
import {
  IntroVideoCta,
  IntroMobileCta,
} from './components/intro-video-cta/intro-video-cta';
import { IntroCompletedOn } from './components/intro-completed-on/intro-completed-on';
import { IntroBelongStandards } from './components/intro-belong-standards/intro-belong-standards';
import { IntroStandardsModal } from './components/intro-standards-modal/intro-standards-modal';
import { WalkthroughExplainer } from './components/walkthrough-explainer/walkthrough-explainer';
import { MoveInOnTrack } from '../Components/move-in-on-track/move-in-on-track';

const BELONG_STANDARDS_VIDEO =
  'https://belong-consumer-assets.s3.us-west-1.amazonaws.com/modals/belong-standards/belong_standard_video.mp4';
const READINESS_WALKTHROUGH_VIDEO =
  'https://belong-consumer-assets.s3.us-west-1.amazonaws.com/improvement-reports/readinesswalkthrough_video_final_720.mp4';

type InspectionType = NonNullable<ReturnType<typeof getInspectionType>>;

const INTRO_TITLE_BY_VARIANT = {
  readinessWalkthrough:
    POST_INSPECTION_FLOW_STRINGS['intro.title.readiness_walkthrough'],
  onboarding: POST_INSPECTION_FLOW_STRINGS['intro.title.onboarding'],
  preMoveOut: POST_INSPECTION_FLOW_STRINGS['intro.title.pre_move_out'],
  moveOut: POST_INSPECTION_FLOW_STRINGS['intro.title.move_out'],
};

const INTRO_COPY_BY_VARIANT = {
  readinessWalkthrough: {
    addressSuffix:
      POST_INSPECTION_FLOW_STRINGS[
        'intro.address_suffix.readiness_walkthrough'
      ],
    ctaLabel: POST_INSPECTION_FLOW_STRINGS['intro.next.readiness_walkthrough'],
    completedOnLabel:
      POST_INSPECTION_FLOW_STRINGS['intro.completed_on.readiness_walkthrough'],
    standardsTitle:
      POST_INSPECTION_FLOW_STRINGS[
        'intro.standards.title.readiness_walkthrough'
      ],
    standardsSubtitle:
      POST_INSPECTION_FLOW_STRINGS[
        'intro.standards.body.readiness_walkthrough'
      ],
    standardsItems:
      POST_INSPECTION_FLOW_STRINGS['intro.divider.readiness_walkthrough'],
    videoSrc: READINESS_WALKTHROUGH_VIDEO,
  },
  standard: {
    addressSuffix: POST_INSPECTION_FLOW_STRINGS['intro.address_suffix'],
    ctaLabel: POST_INSPECTION_FLOW_STRINGS['intro.next'],
    completedOnLabel: POST_INSPECTION_FLOW_STRINGS['intro.completed_on'],
    standardsTitle: AGREEMENT_FLOW_STRINGS.belongStandardsPostInspection,
    standardsSubtitle: undefined,
    standardsItems: POST_INSPECTION_FLOW_STRINGS['intro.divider'],
    videoSrc: BELONG_STANDARDS_VIDEO,
  },
};

function getIntroVariant(inspectionType: InspectionType) {
  if (inspectionType.isReadinessWalkthrough) return 'readinessWalkthrough';
  if (inspectionType.isOnboarding || inspectionType.isAdoptedLease)
    return 'onboarding';
  if (inspectionType.isPreMoveOut) return 'preMoveOut';
  if (inspectionType.isMoveOut) return 'moveOut';

  return undefined;
}

type Props = {
  flow: any;
  onSaveAndNext: () => void;
  stepConfig: any;
  stepConfigs: any;
  stepResource: any;
};

const Intro = ({
  stepResource,
  onSaveAndNext,
  stepConfigs,
  stepConfig,
  flow,
}: Props) => {
  const history = useHistory();
  const isStepCompleted = stepConfig?.status === 'Completed';
  // Bypass the "must watch video" gate on all non-production environments
  // (local, staging, dev1/2/3, etc.) so engineers can test quickly.
  const shouldBypassVideoGate =
    process.env.NEXT_PUBLIC_ENV !== ENVIRONMENTS.PRODUCTION;

  const introVideo = useIntroVideo({
    storageKey: `improvements:${stepConfig?.home?.propertyId ?? 'default'}`,
    initiallyUnlocked: isStepCompleted || shouldBypassVideoGate,
  });

  const [isBelongStandardsModalOpen, setIsBelongStandardsModalOpen] =
    useState(false);
  const { isEnabled: isBelongStandardsEnabled } =
    useFeatureFlag('belong-standards');

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) return;
      if (
        event.data?.type === 'CLOSE_BELONG_STANDARDS_MODAL' &&
        event.data?.source === 'belong-standards-iframe'
      ) {
        setIsBelongStandardsModalOpen(false);
      }
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const areNonHomeowerFixesStepsCompleted = stepConfigs
    ?.filter((step: any) => step.key !== 'homeowner-fixes' && !step.isHidden)
    .every((step: any) => step.status === 'Completed');

  if (areNonHomeowerFixesStepsCompleted) {
    history.push(
      `${BASE_PATHS.HOMEOWNER_SETUP_FLOW}/${stepConfig.home.propertyId}/setup-flow?${QUERY_PARAMS.REPORT_APPROVED}=true`,
    );
    return null;
  }

  const { homeListingDetailsModel } = stepResource;
  // Asserted: the three `inspectionType.…` reads below are unconditional, so a
  // flow with no sub-type already threw here. `getInspectionType` now says it
  // can return `undefined`; this states the assumption the page always made.
  const inspectionType = getInspectionType(flow)!;
  const isWalkthrough = !!inspectionType.isReadinessWalkthrough;
  const homeownerName = homeListingDetailsModel?.owner?.firstName;

  const assessedBy = getImprovementsAssignedEmployee({
    employeeAssignments: homeListingDetailsModel?.employeeAssignments,
    isMoveOut: inspectionType.isMoveOut,
  });

  const variant = getIntroVariant(inspectionType);
  const titleTemplate = variant && INTRO_TITLE_BY_VARIANT[variant];
  const title = titleTemplate
    ? formatString(titleTemplate, { homeownerName })
    : undefined;

  const copy =
    variant === 'readinessWalkthrough'
      ? INTRO_COPY_BY_VARIANT.readinessWalkthrough
      : INTRO_COPY_BY_VARIANT.standard;

  const belongStandardsUrl = `${window.location.origin}/belong-standards?activeModal=BELONG_STANDARDS`;

  return (
    <PostInspectionContentLayout
      stepConfigs={stepConfigs}
      stepConfig={stepConfig}
      title={title}
      showCityBackground={false}
      noOverlay
      employee={assessedBy}
      isLogoVisible={false}
    >
      <IntroAddressHeader
        address={homeListingDetailsModel?.address}
        unitNumber={homeListingDetailsModel?.basicInfo?.unitNumber}
        suffix={copy.addressSuffix}
      />

      <IntroVideoCta
        video={introVideo}
        onContinue={() => onSaveAndNext()}
        ctaLabel={copy.ctaLabel}
        videoSrc={copy.videoSrc}
      />

      {isWalkthrough && (
        <>
          <WalkthroughExplainer />
          <MoveInOnTrack />
        </>
      )}

      <IntroCompletedOn
        completedOn={homeListingDetailsModel?.inspection?.completedOn}
        label={copy.completedOnLabel}
      />

      <Page.OffsetGrid
        ignoreOffset={[SPACE_TYPES.MD, SPACE_TYPES.LG, SPACE_TYPES.XL]}
      >
        <MultipleBelongEmployeeProfileView employees={[assessedBy]} />
      </Page.OffsetGrid>
      <Space value={SPACE_TYPES.XXXL} />

      {isBelongStandardsEnabled && (
        <IntroBelongStandards
          title={copy.standardsTitle}
          subtitle={copy.standardsSubtitle}
          items={copy.standardsItems}
          onLearnMore={() => setIsBelongStandardsModalOpen(true)}
        />
      )}

      <IntroMobileCta
        video={introVideo}
        onContinue={() => onSaveAndNext()}
        ctaLabel={copy.ctaLabel}
      />

      {isBelongStandardsEnabled && (
        <IntroStandardsModal
          isOpen={isBelongStandardsModalOpen}
          onDismiss={() => setIsBelongStandardsModalOpen(false)}
          url={belongStandardsUrl}
        />
      )}
    </PostInspectionContentLayout>
  );
};

export default Intro;
